/**
 * Z01 · Administración del catálogo de zonas comerciales. Solo admin.
 * Permite editar nombre visible, responsable (etiqueta), estado y vigencia.
 * NO concede permisos ni vincula a cuentas de usuario automáticamente.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Loader2, Save, MapPin, ShieldAlert } from "lucide-react";

export default function ZonasAdmin() {
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [draft, setDraft] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/zonas`);
      setZonas(data.zonas || []);
      const d = {};
      (data.zonas || []).forEach((z) => { d[z.codigo] = { nombre_visible: z.nombre_visible, responsable: z.responsable, estado: z.estado }; });
      setDraft(d);
    } catch (e) {
      toast.error("No se pudo cargar el catálogo de zonas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (codigo, field, value) => setDraft((p) => ({ ...p, [codigo]: { ...p[codigo], [field]: value } }));

  const save = async (codigo) => {
    setSaving(codigo);
    try {
      const { data } = await axios.put(`${API}/zonas/${codigo}`, draft[codigo]);
      toast.success(`Zona ${data.zona.nombre_visible} actualizada`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="zonas-admin-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><MapPin size={22} className="text-emerald-600" /> Catálogo de zonas comerciales</h1>
        <p className="text-sm text-slate-500 mt-1">Zonas organizativas y sus responsables. Reutiliza los códigos existentes (ZONA_1/2/3) del campo de las ofertas.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-700 flex items-center gap-2" data-testid="zonas-admin-nota">
        <ShieldAlert size={14} /> El responsable es una <b>etiqueta visible</b>: no concede permisos ni reasigna ofertas. El vínculo a una cuenta de usuario se hará solo con una coincidencia verificada del directorio.
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-400"><Loader2 className="animate-spin mx-auto mb-2" size={24} /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2.5">Código</th>
                <th className="text-left px-4 py-2.5">Nombre visible</th>
                <th className="text-left px-4 py-2.5">Responsable</th>
                <th className="text-left px-4 py-2.5">Estado</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {zonas.map((z) => (
                <tr key={z.codigo} className="border-t border-slate-100" data-testid={`zonas-admin-row-${z.codigo}`}>
                  <td className="px-4 py-2.5 font-mono text-slate-500">{z.codigo}</td>
                  <td className="px-4 py-2.5">
                    <input value={draft[z.codigo]?.nombre_visible || ""} onChange={(e) => setField(z.codigo, "nombre_visible", e.target.value)}
                      data-testid={`zonas-admin-nombre-${z.codigo}`} className="border border-slate-300 rounded px-2 py-1 text-sm w-full" />
                  </td>
                  <td className="px-4 py-2.5">
                    <input value={draft[z.codigo]?.responsable || ""} onChange={(e) => setField(z.codigo, "responsable", e.target.value)}
                      data-testid={`zonas-admin-responsable-${z.codigo}`} className="border border-slate-300 rounded px-2 py-1 text-sm w-full" />
                  </td>
                  <td className="px-4 py-2.5">
                    <select value={draft[z.codigo]?.estado || "activo"} onChange={(e) => setField(z.codigo, "estado", e.target.value)}
                      data-testid={`zonas-admin-estado-${z.codigo}`} className="border border-slate-300 rounded px-2 py-1 text-sm">
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </select>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => save(z.codigo)} disabled={saving === z.codigo} data-testid={`zonas-admin-save-${z.codigo}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-40">
                      {saving === z.codigo ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Guardar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
